import React, { useMemo } from 'react';
import cn from 'classnames';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../common/Input';
import { Label } from '../../../common/Label';
import { DateInput } from '../../../common/DateInput';
import { Field } from '../components/Field';
import { MultiSelectChips } from '../components/MultiSelectChips';
import { SectionHeading } from '../components/SectionHeading';
import { SelectableCard } from '../components/SelectableCard';
import { YesNoRadio } from '../components/YesNoRadio';
import {
  DISABILITY_OPTIONS,
  FAMILY_CONDITIONS,
  FAMILY_MEMBER_OPTIONS,
  HISTORY_SECTION_META,
  type PredefinedFamilyConditionCode,
} from '../constants';
import type {
  DisabilityCode,
  DisabilityState,
  FamilyConditionState,
  FamilyMemberCode,
  HistoryListState,
  HistorySection,
  OtherFamilyEntry,
  OtherFamilyState,
  PreventiveCareData,
} from '../types';

interface AntecedentTabProps {
  family: Record<PredefinedFamilyConditionCode, FamilyConditionState>;
  onToggleFamily: (code: PredefinedFamilyConditionCode) => void;
  onUpdateFamilyMembers: (
    code: PredefinedFamilyConditionCode,
    side: 'wife_members' | 'husband_members',
    next: FamilyMemberCode[]
  ) => void;

  otherFamily: OtherFamilyState;
  onToggleOtherFamily: () => void;
  onAddOtherFamilyEntry: () => void;
  onRemoveOtherFamilyEntry: (id: string) => void;
  onUpdateOtherFamilyEntry: (
    id: string,
    patch: Partial<OtherFamilyEntry>
  ) => void;

  historyLists: Record<HistorySection, HistoryListState>;
  onToggleHistoryList: (section: HistorySection) => void;
  onAddHistoryItem: (section: HistorySection) => void;
  onUpdateHistoryItem: (
    section: HistorySection,
    index: number,
    value: string
  ) => void;
  onRemoveHistoryItem: (section: HistorySection, index: number) => void;

  disability: DisabilityState;
  onToggleDisabilityType: (value: DisabilityCode) => void;
  onClearDisability: () => void;
  onUpdateDisabilityOther: (value: string) => void;

  preventiveCare: PreventiveCareData;
  onUpdatePreventiveCare: <K extends keyof PreventiveCareData>(
    key: K,
    value: PreventiveCareData[K]
  ) => void;
}

export const AntecedentTab: React.FC<AntecedentTabProps> = ({
  family,
  onToggleFamily,
  onUpdateFamilyMembers,
  otherFamily,
  onToggleOtherFamily,
  onAddOtherFamilyEntry,
  onRemoveOtherFamilyEntry,
  onUpdateOtherFamilyEntry,
  historyLists,
  onToggleHistoryList,
  onAddHistoryItem,
  onUpdateHistoryItem,
  onRemoveHistoryItem,
  disability,
  onToggleDisabilityType,
  onClearDisability,
  onUpdateDisabilityOther,
  preventiveCare,
  onUpdatePreventiveCare,
}) => {
  const { t } = useTranslation('Patient');

  const memberOptions = useMemo(
    () =>
      FAMILY_MEMBER_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.labelKey),
      })),
    [t]
  );

  return (
    <div className="space-y-8">
      {/* ---------- Hereditary & family history ---------- */}
      <section>
        <SectionHeading
          icon="👪"
          title={t('dossier_family_history_title')}
          description={t('dossier_family_history_helper')}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {FAMILY_CONDITIONS.map((cond) => {
            const state = family[cond.code];
            return (
              <SelectableCard
                key={cond.code}
                active={state.active}
                onToggle={() => onToggleFamily(cond.code)}
                icon={cond.icon}
                title={t(cond.labelKey)}
                subtitle={t('dossier_family_details_helper')}
              >
                <div>
                  <Label htmlFor={`fh-wife-${cond.code}`}>
                    {t('dossier_wife_side')}
                  </Label>
                  <MultiSelectChips
                    id={`fh-wife-${cond.code}`}
                    value={state.wife_members}
                    options={memberOptions}
                    placeholder={t('dossier_family_member_placeholder')}
                    onChange={(next) =>
                      onUpdateFamilyMembers(cond.code, 'wife_members', next)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`fh-husband-${cond.code}`}>
                    {t('dossier_husband_side')}
                  </Label>
                  <MultiSelectChips
                    id={`fh-husband-${cond.code}`}
                    value={state.husband_members}
                    options={memberOptions}
                    placeholder={t('dossier_family_member_placeholder')}
                    onChange={(next) =>
                      onUpdateFamilyMembers(cond.code, 'husband_members', next)
                    }
                    className="mt-1"
                  />
                </div>
              </SelectableCard>
            );
          })}

          {/* "Autres" — repeatable custom condition rows */}
          <SelectableCard
            active={otherFamily.active}
            onToggle={onToggleOtherFamily}
            icon="📝"
            title={t('dossier_family_other')}
            subtitle={t('dossier_family_other_helper')}
          >
            <div className="space-y-3">
              {otherFamily.entries.length === 0 && (
                <p className="text-xs italic text-title-page-color-300 dark:text-gray-400">
                  {t('dossier_family_other_empty')}
                </p>
              )}
              {otherFamily.entries.map((entry, idx) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-slate-200 bg-white/60 p-3 dark:border-gray-700/70 dark:bg-gray-900/40"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <Input
                      id={`other-label-${entry.id}`}
                      placeholder={t('dossier_family_other_title_placeholder')}
                      value={entry.label}
                      onChange={(e) =>
                        onUpdateOtherFamilyEntry(entry.id, {
                          label: e.target.value,
                        })
                      }
                      className="font-medium"
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveOtherFamilyEntry(entry.id)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 dark:border-gray-700 dark:text-gray-400 dark:hover:border-red-500/70"
                      aria-label={t('dossier_remove_entry')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <Label htmlFor={`other-wife-${entry.id}`}>
                        {t('dossier_wife_side')}
                      </Label>
                      <MultiSelectChips
                        id={`other-wife-${entry.id}`}
                        value={entry.wife_members}
                        options={memberOptions}
                        placeholder={t('dossier_family_member_placeholder')}
                        onChange={(next) =>
                          onUpdateOtherFamilyEntry(entry.id, {
                            wife_members: next,
                          })
                        }
                        className="mt-1"
                        menuPosition={
                          idx === otherFamily.entries.length - 1 ? 'up' : 'down'
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`other-husband-${entry.id}`}>
                        {t('dossier_husband_side')}
                      </Label>
                      <MultiSelectChips
                        id={`other-husband-${entry.id}`}
                        value={entry.husband_members}
                        options={memberOptions}
                        placeholder={t('dossier_family_member_placeholder')}
                        onChange={(next) =>
                          onUpdateOtherFamilyEntry(entry.id, {
                            husband_members: next,
                          })
                        }
                        className="mt-1"
                        menuPosition={
                          idx === otherFamily.entries.length - 1 ? 'up' : 'down'
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={onAddOtherFamilyEntry}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                <Plus size={14} />
                {t('dossier_family_other_add')}
              </button>
            </div>
          </SelectableCard>
        </div>
      </section>

      {/* ---------- Antécédents de la femme ---------- */}
      <section>
        <SectionHeading
          icon="♿"
          title={t('dossier_disability_title')}
          description={t('dossier_disability_title_helper')}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {HISTORY_SECTION_META.map((meta) => {
            const state = historyLists[meta.code];
            return (
              <SelectableCard
                key={meta.code}
                active={state.active}
                onToggle={() => onToggleHistoryList(meta.code)}
                icon={meta.icon}
                title={t(meta.titleKey)}
                subtitle={t(meta.helperKey)}
              >
                <div className="space-y-2">
                  {state.items.map((item, idx) => (
                    <div
                      key={`${meta.code}-${idx}`}
                      className="flex items-center gap-2"
                    >
                      <Input
                        id={`hx-${meta.code}-${idx}`}
                        placeholder={t(meta.placeholderKey)}
                        value={item}
                        onChange={(e) =>
                          onUpdateHistoryItem(meta.code, idx, e.target.value)
                        }
                      />
                      {state.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onRemoveHistoryItem(meta.code, idx)}
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 dark:border-gray-700 dark:text-gray-400 dark:hover:border-red-500/70"
                          aria-label={t('dossier_remove_entry')}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => onAddHistoryItem(meta.code)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Plus size={14} />
                    {t('dossier_add_entry')}
                  </button>
                </div>
              </SelectableCard>
            );
          })}

          {/* Disability — multi-select handicap (+ optional free text for OTHER) */}
          <div
            className={cn(
              'flex flex-col rounded-xl border p-4 transition-all',
              disability.types.length > 0
                ? 'border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/20 dark:bg-blue-900/20'
                : 'border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800/40'
            )}
          >
            <div className="mb-3 flex items-start gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-base dark:bg-gray-700/60">
                ♿
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
                  {t('dossier_disability_section_title')}
                </div>
                <div className="mt-0.5 text-xs leading-snug text-title-page-color-300 dark:text-gray-400">
                  {t('dossier_disability_section_helper')}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {DISABILITY_OPTIONS.map((opt) => {
                const checked = disability.types.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                      checked
                        ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
                        : 'text-title-page-color-100 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700/60'
                    )}
                  >
                    <input
                      type="checkbox"
                      name="disability-type"
                      checked={checked}
                      onChange={() => onToggleDisabilityType(opt.value)}
                      className="accent-blue-500"
                    />
                    <span className="truncate">{t(opt.labelKey)}</span>
                  </label>
                );
              })}
              {disability.types.length > 0 && (
                <button
                  type="button"
                  onClick={onClearDisability}
                  className="text-xs text-slate-500 hover:text-red-600 dark:text-gray-400"
                >
                  {t('dossier_remove_entry')}
                </button>
              )}
            </div>

            {disability.types.includes('OTHER') && (
              <div className="mt-3">
                <Field
                  id="disability-other"
                  label={t('dossier_disability_other_label')}
                >
                  <Input
                    id="disability-other"
                    placeholder={t('dossier_disability_other_placeholder')}
                    value={disability.other_description}
                    onChange={(e) => onUpdateDisabilityOther(e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Preventive care ---------- */}
      <section>
        <SectionHeading
          icon="💉"
          title={t('dossier_preventive_care_title')}
          description={t('dossier_preventive_care_helper')}
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
            <div className="mb-4">
              <div className="text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
                {t('dossier_vat_title')}
              </div>
              <p className="mt-1 text-xs text-title-page-color-300 dark:text-gray-400">
                {t('dossier_vat_helper')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((dose) => {
                const selected = preventiveCare.vat_dose === dose;
                return (
                  <button
                    key={dose}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      onUpdatePreventiveCare(
                        'vat_dose',
                        selected ? null : dose
                      )
                    }
                    className={cn(
                      'h-10 min-w-10 rounded-lg border px-3 text-sm font-semibold transition-colors',
                      selected
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-slate-300 bg-white text-title-page-color-100 hover:border-blue-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                    )}
                  >
                    {dose}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
            <Field
              id="cervical-smear-iva-date"
              label={t('dossier_cervical_smear_iva_date')}
              hint={t('dossier_cervical_smear_iva_helper')}
            >
              <DateInput
                id="cervical-smear-iva-date"
                value={preventiveCare.cervical_smear_iva_date}
                onChange={(event) =>
                  onUpdatePreventiveCare(
                    'cervical_smear_iva_date',
                    event.target.value
                  )
                }
              />
            </Field>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
            <Field
              id="rubella-vaccinated"
              label={t('dossier_rubella_vaccinated')}
            >
              <YesNoRadio
                name="rubella-vaccinated"
                value={preventiveCare.rubella_vaccinated}
                onChange={(value) =>
                  onUpdatePreventiveCare('rubella_vaccinated', value)
                }
              />
            </Field>
            {preventiveCare.rubella_vaccinated && (
              <div className="mt-4">
                <Field
                  id="rubella-vaccination-date"
                  label={t('dossier_rubella_vaccination_date')}
                >
                  <DateInput
                    id="rubella-vaccination-date"
                    value={preventiveCare.rubella_vaccination_date}
                    onChange={(event) =>
                      onUpdatePreventiveCare(
                        'rubella_vaccination_date',
                        event.target.value
                      )
                    }
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/40">
            <Field
              id="hepatitis-b-vaccinated"
              label={t('dossier_hepatitis_b_vaccinated')}
            >
              <YesNoRadio
                name="hepatitis-b-vaccinated"
                value={preventiveCare.hepatitis_b_vaccinated}
                onChange={(value) =>
                  onUpdatePreventiveCare('hepatitis_b_vaccinated', value)
                }
              />
            </Field>
            {preventiveCare.hepatitis_b_vaccinated && (
              <div className="mt-4">
                <Field
                  id="hepatitis-b-vaccination-date"
                  label={t('dossier_hepatitis_b_vaccination_date')}
                >
                  <DateInput
                    id="hepatitis-b-vaccination-date"
                    value={preventiveCare.hepatitis_b_vaccination_date}
                    onChange={(event) =>
                      onUpdatePreventiveCare(
                        'hepatitis_b_vaccination_date',
                        event.target.value
                      )
                    }
                  />
                </Field>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
